import { Vector2 } from "../../../common/vector2.js";
import { Game } from "../../../game.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ADVENTURE_REWARD_ICONS, type AdventureBoxReward } from "../../../adventure/adventure_box_rewards.js";
import { rewardAmountText } from "../../../common/reward_format.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import type { GameStatistics } from "../../../storage/statistics_storage.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { RewardFrame } from "../../common_ui_elements/reward_frame.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UICanvasRect } from "../../common_ui_elements/ui_canvas_rect.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { TournamentTitle } from "../../dialog/tournament_title.js";
import { RewardFlySource, RewardFlyToHud } from "../../reward_fly/reward_fly_to_hud.js";
import { AnchoredTooltip } from "../../tooltip/anchored_tooltip.js";
import { RewardTooltip } from "../../tooltip/reward_tooltip.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";

export interface TournamentRewardOverlayParams
{
  reward: AdventureBoxReward;
  on_closed?: () => void;
  /** Collects the reward on the server; the fly animation plays only when it resolves true. */
  on_claim?: () => Promise<boolean>;
}

/**
 * Shown over the menu once the tournament bar reached a reward: the tournament
 * banner, the reward card and "Claim". Claiming flies the reward to the HUD
 * and closes the overlay.
 */
export class TournamentRewardOverlay extends AbstractOverlay
{
  private static readonly FRAME_W = 1080;
  private static readonly FRAME_H = 1920;
  private static readonly BANNER = { x: 126, y: 180, w: 829, h: 277 };
  private static readonly TITLE_CENTER_Y = 294.5;
  private static readonly CARD = { center_y: 957, w: 224, h: 276, icon: 190, coin_w: 209, coin_h: 168, amount_font: 48, amount_offset_y: 14 };
  private static readonly BUTTON = { y: 1420, w: 560, h: 256, font: 80 };
  private static readonly APPEAR_MS = 200;
  private static readonly CARD_DELAY_MS = 120;
  private static readonly CARD_POP_MS = 180;
  private static readonly CARD_SETTLE_MS = 110;
  private static readonly BUTTON_DELAY_MS = 260;
  private static readonly FADE_MS = 160;
  private static readonly COIN_FLY_SPEED = 1.5;

  private readonly design_x: number;
  private readonly design_y: number;
  private readonly overlay_params: TournamentRewardOverlayParams;
  private readonly stats_before: GameStatistics;
  private backdrop!: UICanvasRect;
  private banner!: UIImage;
  private title_parts: UIElement[] = [];
  private frame!: RewardFrame;
  private claim_button!: UINineSliceElement;
  private claimed = false;
  private close_notified = false;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, params: TournamentRewardOverlayParams, overlay_id: OverlayId )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );
    this.overlay_params = params;
    this.stats_before = StatisticsStorage.load();
    this.design_x = ( this.rect.getW() - LayoutScaler.widthFromDesign( TournamentRewardOverlay.FRAME_W ) ) / 2;
    this.design_y = ( this.rect.getH() - LayoutScaler.heightFromDesign( TournamentRewardOverlay.FRAME_H ) ) / 2;
    this.createBackdrop();
    this.createBanner();
    this.createCard();
    this.createClaimButton();
    this.playAppear();
  }

  public name(): string { return "TournamentRewardOverlay"; }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  public override getViewportBackground(): string | null
  {
    return "transparent";
  }

  public override onOverlayWillClose(): void
  {
    AnchoredTooltip.hide();
    if ( this.close_notified ) return;
    this.close_notified = true;
    this.overlay_params.on_closed?.();
  }

  private x( design_x: number ): number
  {
    return this.design_x + LayoutScaler.xFromDesign( design_x );
  }

  private y( design_y: number ): number
  {
    return this.design_y + LayoutScaler.yFromDesign( design_y );
  }

  private centeredX( design_w: number ): number
  {
    return this.x( ( TournamentRewardOverlay.FRAME_W - design_w ) / 2 );
  }

  private createBackdrop(): void
  {
    this.backdrop = new UICanvasRect(
      this.ctx,
      -Game.safeAreaLeft(),
      -Game.safeAreaTop(),
      this.rect.getW() + Game.safeAreaLeft() + Game.safeAreaRight(),
      this.rect.getH() + Game.safeAreaTop() + Game.safeAreaBottom(),
      "rgba(0, 0, 0, 0.8)",
    );
    this.backdrop.setOpacityImmediate( 0 );
    this.backdrop.setOnClick( () => {} );
    this.addChild( this.backdrop );
  }

  private createBanner(): void
  {
    const banner = TournamentRewardOverlay.BANNER;
    this.banner = new UIImage(
      this.ctx,
      this.x( banner.x ),
      this.y( banner.y ),
      LayoutScaler.widthFromDesign( banner.w ),
      LayoutScaler.heightFromDesign( banner.h ),
      ImgID.UI_TournamentTopSubstrate,
    );
    this.banner.setOpacityImmediate( 0 );
    this.addChild( this.banner );

    const title_x = this.centeredX( TournamentTitle.TITLE_W );
    const title_y = this.y( TournamentRewardOverlay.TITLE_CENTER_Y - TournamentTitle.TITLE_H / 2 );
    for ( const layer of TournamentTitle.createTitleLayers( this.ctx, title_x, title_y ) )
    {
      layer.setOpacityImmediate( 0 );
      this.addChild( layer );
      this.title_parts.push( layer );
    }
  }

  private createCard(): void
  {
    const card = TournamentRewardOverlay.CARD;
    const reward = this.overlay_params.reward;
    const is_coin = reward.type === "coin";
    this.frame = new RewardFrame( this.ctx, this.centeredX( card.w ), this.y( card.center_y - card.h / 2 ), {
      icon_id: is_coin ? ImgID.UI_ShopBlockImg : ADVENTURE_REWARD_ICONS[reward.type],
      amount_text: is_coin ? String( reward.amount ) : rewardAmountText( reward.type, reward.amount ),
      design_w: card.w,
      design_h: card.h,
      icon_width: LayoutScaler.widthFromDesign( is_coin ? card.coin_w : card.icon ),
      icon_height: LayoutScaler.heightFromDesign( is_coin ? card.coin_h : card.icon ),
      amount_font_size: card.amount_font,
      amount_offset_y: card.amount_offset_y,
    } );
    this.frame.setOpacityImmediate( 0 );
    this.frame.setScaleImmediate( new Vector2( 0, 0 ) );
    RewardTooltip.attachLegacy( this.frame, reward.type );
    this.addChild( this.frame );
  }

  private createClaimButton(): void
  {
    const button = TournamentRewardOverlay.BUTTON;
    const w = LayoutScaler.widthFromDesign( button.w );
    const h = LayoutScaler.heightFromDesign( button.h );
    this.claim_button = new UINineSliceElement(
      this.ctx,
      this.centeredX( button.w ),
      this.y( button.y ),
      w,
      h,
      ImgID.UI_GreenFrameBtn,
      "frame_btn",
      undefined,
      undefined,
      true,
    );
    this.claim_button.addChild( new UITextElement( this.ctx, 0, LayoutScaler.yFromDesign( 4 ), w, h, {
      localization_key: "CLAIM",
      font_size: LayoutScaler.textSizeFromDesign( button.font ),
      min_font_size: LayoutScaler.textSizeFromDesign( 44 ),
      font_weight: 900,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      auto_wrap: false,
      scale_to_width: true,
      ...TextStyleScheme.buttonShadow(),
      outline_color: ColorScheme.more_lives_buy_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      outline_offset_y: LayoutScaler.yFromDesign( 2 ),
    } ), true );
    this.claim_button.setOpacityImmediate( 0 );
    this.claim_button.setClickable( false );
    this.claim_button.setOnClick( () => this.claim() );
    this.addChild( this.claim_button );
  }

  private playAppear(): void
  {
    const now = performance.now();
    const ms = TournamentRewardOverlay.APPEAR_MS;
    this.backdrop.fadeIn( ms, now, Easing.EaseOutQuad );
    for ( const element of [ this.banner, ...this.title_parts ] )
      element.fadeIn( ms, now, Easing.EaseOutQuad );

    const pop = TournamentRewardOverlay.CARD_POP_MS;
    const card_at = now + TournamentRewardOverlay.CARD_DELAY_MS;
    this.frame.fadeIn( pop, card_at, Easing.EaseOutQuad );
    this.frame.addTransform( new TransformScale( this.frame, true, new Vector2( 0, 0 ), new Vector2( 1.15, 1.15 ), card_at, pop, { easing: Easing.EaseOutQuad } ) );
    this.frame.addTransform( new TransformScale( this.frame, true, new Vector2( 1.15, 1.15 ), new Vector2( 1, 1 ), card_at + pop,
      TournamentRewardOverlay.CARD_SETTLE_MS, { easing: Easing.EaseInQuad } ) );

    const button_at = card_at + TournamentRewardOverlay.BUTTON_DELAY_MS;
    this.claim_button.fadeIn( 180, button_at, Easing.EaseOutQuad );
    RewardFlyToHud.schedule( this.ctx, this, now, button_at, () => this.claim_button.setClickable( true ) );
  }

  private claim(): void
  {
    if ( this.claimed ) return;
    this.claimed = true;
    this.claim_button.setClickable( false );
    const request = this.overlay_params.on_claim ? this.overlay_params.on_claim() : Promise.resolve( true );
    void request.then( ok =>
    {
      if ( ok ) this.playClaim();
      else if ( OverlayManager.isOverlayOpen( this.getId() ) ) OverlayManager.closeOverlay();
    } );
  }

  private playClaim(): void
  {

    const reward = this.overlay_params.reward;
    const is_heart = reward.type === "heart" || reward.type === "infinite_hearts";
    const pos = this.frame.icon.getAbsolutePosition();
    const rect = this.frame.icon.getRect();
    const source: RewardFlySource = {
      target: reward.type === "coin" ? "coin" : is_heart ? "heart" : "avatar",
      img_id: ADVENTURE_REWARD_ICONS[reward.type],
      center: new Vector2( pos.x + rect.getW() / 2, pos.y + rect.getH() / 2 ),
      size: rect.getW(),
      amount: reward.amount,
      copies: reward.type === "heart" ? Math.max( 1, Math.min( 3, reward.amount ) ) : 1,
    };

    const now = performance.now();
    const fade = TournamentRewardOverlay.FADE_MS;
    for ( const element of [ this.frame, this.claim_button, this.banner, ...this.title_parts, this.backdrop ] )
      element.fadeOut( fade, now, Easing.EaseInQuad );
    const finish = RewardFlyToHud.play( this.ctx, this, [ source ], this.stats_before, TournamentRewardOverlay.COIN_FLY_SPEED );
    RewardFlyToHud.schedule( this.ctx, this, now, Math.max( finish, now + fade ), () =>
    {
      if ( OverlayManager.isOverlayOpen( this.getId() ) )
        OverlayManager.closeOverlay();
    } );
  }
}
